import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { GradeBadge, GedPrepDisclaimer } from "@/components/common";
import { MessageCircleQuestion, BookOpenCheck, Users, School, Sparkles, ShieldCheck, ArrowRight, CheckCircle2, Building2, ScrollText, RefreshCw } from "lucide-react";

const BANDS = [
  { g: "Pre-K", t: "Letters, counting, colors & curiosity" },
  { g: "K-2", t: "Early reading, adding & animal worlds" },
  { g: "3-5", t: "Fractions, comprehension & the solar system" },
  { g: "6-8", t: "Pre-algebra, cells & ancient civilizations" },
  { g: "9-12", t: "Algebra, chemistry & U.S. history" },
  { g: "College", t: "Calculus, psychology & economics" },
];

const FEATURES = [
  { icon: MessageCircleQuestion, title: "An AI tutor that teaches, not tells", text: "Sage guides students step by step with questions and hints, matched to their grade level." },
  { icon: Sparkles, title: "Lessons on any topic, in seconds", text: "Describe a topic and get a full lesson with objectives, vocabulary and a graded quiz." },
  { icon: BookOpenCheck, title: "A ready starter library", text: "Structured courses from Pre-K to College, ready on day one." },
  { icon: Users, title: "Parents see real progress", text: "Link to your child with a code and follow lessons, quiz scores and weekly activity." },
  { icon: School, title: "Built for classrooms too", text: "Teachers create classes, share a join code, assign lessons and track a live gradebook." },
  { icon: ShieldCheck, title: "Safe, age-appropriate learning", text: "Every role sees only what it should. Content stays educational and age-appropriate." },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#FBFAF4]">
      <header className="sticky top-0 z-30 bg-[#FBFAF4] border-b border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-2">
            {user ? (
              <Button asChild data-testid="landing-dashboard-button"><Link to="/dashboard">Go to dashboard</Link></Button>
            ) : (
              <>
                <Button variant="ghost" asChild data-testid="landing-login-button"><Link to="/login">Sign in</Link></Button>
                <Button asChild data-testid="landing-signup-button"><Link to="/signup">Get started free</Link></Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="hero-wash">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-3 py-1 text-xs font-medium text-[#0B5B5B] mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Pre-K through College · Home school & schools
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tight text-[#071A22] leading-[1.05]">
              One online school for every stage of learning.
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Lessons, quizzes and a patient AI tutor for home school families — and a teaching assistant schools can put to work for tutoring and classes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" asChild className="h-12 px-6" data-testid="hero-get-started-button">
                <Link to={user ? "/dashboard" : "/signup"}>Start learning <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="h-12 px-6 bg-white" data-testid="hero-teacher-button">
                <Link to={user ? "/dashboard" : "/signup?role=teacher"}>I'm a teacher</Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#123645]">
              {["Socratic AI tutor", "Parent progress view", "Class gradebook"].map((t) => (
                <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#1F9D55]" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 relative fade-up">
            <img
              src="https://images.pexels.com/photos/8121104/pexels-photo-8121104.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Tutor helping a student at home"
              className="w-full h-[340px] sm:h-[440px] object-cover rounded-[18px] border border-border shadow-[var(--shadow-md)]"
            />
            <div className="absolute -bottom-6 left-4 sm:-left-6 bg-white border border-border rounded-[14px] p-4 shadow-[var(--shadow-md)] max-w-[280px]">
              <div className="text-xs text-muted-foreground mb-1">Sage · AI tutor</div>
              <div className="text-sm text-[#071A22]">Good thinking! What do you get if you add one more group of 7 to 7 × 7?</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-sm font-display uppercase tracking-[0.14em] text-muted-foreground">Every grade, one place</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BANDS.map((b) => (
            <div key={b.g} className="bg-white border border-border rounded-[14px] p-5 lift" data-testid={`landing-band-${b.g}`}>
              <GradeBadge grade={b.g} />
              <div className="mt-3 text-[#071A22] font-medium">{b.t}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <h2 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight max-w-2xl">Everything a learner, parent and teacher needs.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-[14px] p-6 bg-[#FBFAF4] border border-border lift">
                <div className="h-11 w-11 rounded-xl bg-[#D7F3F1] flex items-center justify-center"><f.icon className="h-5 w-5 text-[#0B6B6B]" /></div>
                <div className="mt-4 font-display font-semibold text-lg">{f.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <img
          src="https://images.pexels.com/photos/9159042/pexels-photo-9159042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Students learning together"
          className="w-full h-[320px] object-cover rounded-[18px] border border-border"
        />
        <div>
          <h2 className="text-3xl font-display font-semibold tracking-tight">How it works</h2>
          <ol className="mt-6 space-y-5">
            {[
              ["Create your account", "Pick student, parent or teacher. Students choose their grade level."],
              ["Learn or teach", "Open a course, read the lesson, ask Sage when stuck, then take the quiz."],
              ["Connect & track", "Parents link with a child's code. Teachers share a class join code and assign lessons."],
            ].map(([t, d], i) => (
              <li key={t} className="flex gap-4">
                <div className="h-9 w-9 shrink-0 rounded-full bg-[#0F7B7D] text-white font-display font-semibold flex items-center justify-center">{i + 1}</div>
                <div>
                  <div className="font-semibold">{t}</div>
                  <div className="text-sm text-muted-foreground mt-1">{d}</div>
                </div>
              </li>
            ))}
          </ol>
          <Button size="lg" asChild className="mt-8 h-12" data-testid="how-get-started-button"><Link to={user ? "/dashboard" : "/signup"}>Create a free account</Link></Button>
        </div>
      </section>

      <section className="bg-white border-t border-border" id="pricing">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <h2 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight">Simple pricing. 30 days free.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">Start with a 30-day free trial on any plan. Pay monthly, or yearly and get 2 months free. Cancel anytime.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Users, name: "Family", price: "$20.99", unit: "per child / month", year: "or $209.90 per child / year", points: ["Parent account included", "Full library Pre-K to College", "Unlimited AI tutoring & AI lessons", "Progress dashboard & home school classes"], cta: "/signup?role=parent", id: "family" },
              { icon: School, name: "Classroom", price: "$14.99", unit: "per student / month", year: "50+ students $10.99 · 100+ students $5.99 / month · Teacher account free", points: ["Teacher account included free", "Classes, join codes, assignments & gradebook", "Volume discount kicks in at 50 students", "Yearly: 2 months free"], cta: "/signup?role=teacher", id: "classroom" },
              { icon: Building2, name: "School", price: "$14.99", unit: "per student / month", year: "50+ students $10.99 · 100+ students $5.99 · Unlimited teachers free", points: ["School account with join code & email invites", "Unlimited teachers, one shared seat pool", "See every teacher's classes", "Yearly: 2 months free"], cta: "/signup?role=school", id: "school" },
            ].map((p) => (
              <div key={p.name} className="rounded-[18px] border border-border bg-[#FBFAF4] p-7 lift" data-testid={`landing-plan-${p.id}`}>
                <div className="flex items-center gap-3"><div className="h-11 w-11 rounded-xl bg-[#D7F3F1] flex items-center justify-center"><p.icon className="h-5 w-5 text-[#0B6B6B]" /></div><div className="font-display font-semibold text-xl">{p.name}</div></div>
                <div className="mt-5 flex items-end gap-2"><span className="text-4xl font-display font-semibold">{p.price}</span><span className="text-sm text-muted-foreground pb-1">{p.unit}</span></div>
                <div className="text-xs text-muted-foreground mt-1">{p.year}</div>
                <ul className="mt-5 space-y-2 text-sm">{p.points.map((t) => <li key={t} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#1F9D55] mt-0.5 shrink-0" />{t}</li>)}</ul>
                <Button asChild className="mt-6 w-full h-11" data-testid={`landing-plan-${p.id}-button`}><Link to={user ? "/billing" : p.cta}>Start 30-day free trial</Link></Button>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">A card is required to start the trial. You won't be charged until day 31.</p>

          <div className="mt-10 rounded-[20px] border border-border bg-white p-7 lg:p-9 lift" data-testid="landing-plan-ged">
            <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8 items-start">
              <div>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#FFF1C7] flex items-center justify-center"><ScrollText className="h-5 w-5 text-[#7A5A00]" /></div>
                  <div>
                    <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground">For adult learners</div>
                    <div className="font-display font-semibold text-2xl">GED Prep</div>
                  </div>
                </div>
                <GedPrepDisclaimer className="mt-4" />
                <p className="mt-4 text-[15px] text-muted-foreground max-w-xl">
                  A separate adult-learner track — a 12-month program with four GED-aligned courses, Sage the AI tutor, and optional timed practice tests. Pay once, no subscription, no free trial.
                </p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[
                    "4 standards-aligned courses (Math, RLA, Science, Social)",
                    "12 months of lessons + Sage AI tutor",
                    "Readiness estimates on the GED 100-200 scale",
                    "Timed practice tests sold separately",
                  ].map((t) => (
                    <li key={t} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#1F9D55] mt-0.5 shrink-0" />{t}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-[16px] bg-[#FBFAF4] border border-border p-5">
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-display font-semibold" data-testid="landing-ged-base-price">$50</span>
                  <span className="text-sm text-muted-foreground pb-1">one-time · 12 months of access</span>
                </div>
                <div className="mt-4 pt-4 border-t border-border space-y-2.5 text-sm">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex items-start gap-2"><Sparkles className="h-4 w-4 text-[#7A5A00] mt-0.5 shrink-0" /><span>Practice Tests pack <span className="text-muted-foreground">· one attempt per subject</span></span></div>
                    <span className="font-display font-semibold whitespace-nowrap" data-testid="landing-ged-pack-price">+ $20</span>
                  </div>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex items-start gap-2"><RefreshCw className="h-4 w-4 text-[#0B6B6B] mt-0.5 shrink-0" /><span>Retake any subject <span className="text-muted-foreground">· pay only when you want another try</span></span></div>
                    <span className="font-display font-semibold whitespace-nowrap" data-testid="landing-ged-retest-price">+ $5</span>
                  </div>
                </div>
                <Button asChild className="mt-5 w-full h-11" data-testid="landing-plan-ged-button">
                  <Link to={user ? "/ged" : "/signup?role=student&next=/ged"}>Unlock GED Prep</Link>
                </Button>
                <p className="mt-3 text-[11px] text-muted-foreground">One-time payment. No subscription, no free trial. Available to anyone, including existing subscribers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-[#F5F1E6]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row justify-between gap-4 text-sm text-muted-foreground">
          <Brand />
          <div className="flex flex-wrap gap-4">
            <Link to="/syllabi" className="hover:text-[#0B6B6B]" data-testid="footer-syllabi-link">Course syllabi</Link>
            <Link to="/policies" className="hover:text-[#0B6B6B]" data-testid="footer-policies-link">School policies</Link>
          </div>
          <div>Online learning from Pre-K to College · © {new Date().getFullYear()} Scholar Campus</div>
        </div>
      </footer>
    </div>
  );
}
